import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { PermissionGuard } from 'app/core/auth/guards/permission.guard';
import { ADMIN_MODULE_IDS } from 'app/core/admin-module-ids';
import { LayoutComponent } from 'app/layout/layout.component';

// prettier-ignore
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/project'
    { path: '', pathMatch: 'full', redirectTo: 'dashboards/project' },

    // Redirect signed-in user to the '/dashboards/project'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'dashboards/project',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'otp-verification',
                loadChildren: () =>
                    import(
                        'app/modules/auth/otp-verification/otp-verification.routes'
                    ),
            },

            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: '2fa-verification',
                loadChildren: () =>
                    import('app/modules/auth/2fa-verification/2fa-verification.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
            // The sales-partner app's WebView of the category × module
            // matrix. No AuthGuard and no admin shell: partners are not
            // admin users, and the 30-minute token in the URL is the whole
            // ticket (checked by the API, not the router).
            {
                path: 'embed/category-module-map',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/category-module-map/category-module-map-embed.routes'
                    ),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'data-usage-analysis',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/data-usage-analysis/data-usage-analysis.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 2, requiredPermission: 'View' }
            },
             {
                path: 'change-logs',
                loadChildren: () =>
                    import('app/modules/admin/pages/change-logs/change-logs.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 61, requiredPermission: 'View' }
            },
            {
                path: 'error-logs',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/activity-logs/activity-logs.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 63, requiredPermission: 'View' }
            },
            {
                path: 'error-logs/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/activity-logs/activity-log-details/activity-log-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 63, requiredPermission: 'View' }
            },
            {
                path: 'business',
                loadChildren: () =>
                    import('app/modules/admin/pages/business/business.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 3, requiredPermission: 'View' }
            },
            {
                // Demo Bookings — reuses the Businesses module permission (3) so
                // no new admin module needs seeding.
                path: 'app-demo',
                loadChildren: () =>
                    import('app/modules/admin/pages/app-demo/app-demo.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 75, requiredPermission: 'View' }
            },
            {
                path: 'business/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-detail/business-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 3, requiredPermission: 'View' }
            },
            {
                path: 'blogs',
                loadChildren: () =>
                    import('app/modules/admin/pages/blog/blog.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 12, requiredPermission: 'View' }
            },

            {
                path: 'blogs/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/blog/blog-description/blog-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 12, requiredPermission: 'View' }
            },
            {
                path: 'blogs/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/blog/blog-description/blog-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 12, requiredPermission: 'Edit' }
            },
            {
                path: 'blogs/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/blog/blog-description/blog-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 12, requiredPermission: 'Add' }
            },
            {
                path: 'coupon',
                loadChildren: () =>
                    import('app/modules/admin/pages/coupon/coupon.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 8, requiredPermission: 'View' }
            },
            {
                // moduleId 74 must match the seeded a_modules row, or the guard
                // denies everyone and the page reads as missing rather than
                // forbidden.
                path: 'onboarding-reminders',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/onboarding-reminders/onboarding-reminders.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 74, requiredPermission: 'View' }
            },
            {
                // MUST stay above the ':id' route below, or 'configuration'
                // is matched as a reminder id and the detail page 404s.
                path: 'onboarding-reminders/configuration',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/onboarding-reminders/onboarding-reminder-config/onboarding-reminder-config.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 74, requiredPermission: 'View' }
            },
            {
                // Who will be messaged. Also above ':id', for the same reason.
                path: 'onboarding-reminders/recipients',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/onboarding-reminders/onboarding-reminder-recipients/onboarding-reminder-recipients.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 74, requiredPermission: 'View' }
            },
            {
                path: 'onboarding-reminders/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/onboarding-reminders/onboarding-reminder-detail/onboarding-reminder-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 74, requiredPermission: 'View' }
            },
            {
                path: 'coupon/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/coupon/coupon-detail/coupon-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 8, requiredPermission: 'View' }
            },
            {
                path: 'coupon/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/coupon-crud/coupon-crud.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 8, requiredPermission: 'Edit' }
            },

            {
                path: 'coupon/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/coupon-crud/coupon-crud.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 8, requiredPermission: 'Add' }
            },
            {
                path: 'dashboard',
                loadChildren: () =>
                    import(
                        'app/modules/admin/dashboards/dashboard/dashboard.routes'
                    ),
            },
            {
                // The admin's own inbox (behind the header bell). No
                // PermissionGuard, like Dashboard: it is personal, and the
                // API scopes rows to the calling admin. `/notifications` is
                // already the User Notifications campaign tool — hence the
                // prefix.
                path: 'admin-notifications',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-notifications/admin-notifications.routes'
                    ),
            },
            {
                path: 'analytics',
                loadChildren: () =>
                    import(
                        'app/modules/admin/dashboards/analytics/analytics.routes'
                    ),
            },
            {
                path: 'profile/2fa-setup',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/settings/2fa-setup/2fa-setup.routes'
                    ),
            },
            {
                path: 'product-analytics',
                redirectTo: 'activity-logs',
                pathMatch: 'full',
            },
            {
                path: 'activity-logs',
                loadChildren: () =>
                    import(
                        'app/modules/admin/dashboards/product-analytics/product-analytics.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 1, requiredPermission: 'View' },
            },
            {
                path: 'profile',
                loadChildren: () =>
                    import('app/modules/admin/pages/user-profile/user-profile.routes'),
            },
            {
                path: 'profile-details',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/profile-detail/profile-detail.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: '2fa-setup',
                redirectTo: 'profile/2fa-setup',
                pathMatch: 'full',
            },
            {
                path: 'orders',
                loadChildren: () =>
                    import('app/modules/admin/pages/order/order.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 9, requiredPermission: 'View' }
            },
            {
                path: 'orders/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/order/order-detail/order-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 9, requiredPermission: 'View' }
            },
            {
                path: 'orders/refund/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/order-refund/order-refund.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 10, requiredPermission: 'Edit' }
            },
            {
                path: 'order-refunds',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/order-refund/order-refund.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 10, requiredPermission: 'View' }
            },
            {
                path: 'support-articles',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-article/support-article.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 28, requiredPermission: 'View' }
            },

            {
                path: 'support-articles/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-article/support-article-description/support-article-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 28, requiredPermission: 'View' }
            },
            {
                path: 'support-articles/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-article/support-article-description/support-article-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 28, requiredPermission: 'Edit' }
            },
            {
                path: 'support-articles/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-article/support-article-description/support-article-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 28, requiredPermission: 'Add' }
            },
            {
                path: 'testimonital',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/testimonial/testimonial.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 17, requiredPermission: 'View' }
            },
            {
                path: 'inquiries',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/inquiries/inquiries.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 6, requiredPermission: 'View' }
            },
            {
                path: 'inquiries/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/inquiries/inquiry-details/inquiry-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 6, requiredPermission: 'View' }
            },
            {
                path: 'feedback',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feedbacks/feedbacks.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 18, requiredPermission: 'View' }
            },
            {
                path: 'chat-support',
                loadChildren: () =>
                    import('app/modules/admin/pages/chat-Support/chat.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 18, requiredPermission: 'View' }
            },
            {
                path: 'support-actions',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-Actions/support-action.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 16, requiredPermission: 'View' }
            },
            {
                path: 'support-actions/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-Actions/support-action-details/support-action-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 16, requiredPermission: 'View' }
            },
            {
                path: 'support-actions/add',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-Actions/support-action-details/support-action-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 16, requiredPermission: 'Add' }
            },
            {
                path: 'support-actions/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-Actions/support-action-details/support-action-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 16, requiredPermission: 'Edit' }
            },
            {
                path: 'notifications',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/notifications/notification.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 19, requiredPermission: 'View' }
            },
            // {
            //     path: 'notifications/view/:id',
            //     loadChildren: () =>
            //         import(
            //             'app/modules/admin/pages/notifications/notification-details/notification-details.routes'
            //         ),
            //     canActivate: [PermissionGuard],
            //     data: { moduleId: 19, requiredPermission: 'View' }
            // },
            {
                path: 'notifications/add',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/notifications/notification-details/notification-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 19, requiredPermission: 'Add' }
            },
            {
                path: 'notifications/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/notifications/notification-details/notification-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 19, requiredPermission: 'Edit' }
            },
            {
                path: 'invoice-templates',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/invoice-templates/invoice-templates.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 30, requiredPermission: 'View' }
            },
            {
                path: 'invoice-templates/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/invoice-templates/invoice-templates-description/invoice-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 30, requiredPermission: 'View' }
            },
            {
                path: 'invoice-templates/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/invoice-templates/invoice-templates-description/invoice-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 30, requiredPermission: 'Edit' }
            },
            {
                path: 'email-templates',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/email-templates/email-templates.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 60, requiredPermission: 'View' }
            },
            {
                path: 'email-templates/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/email-templates/email-templates-description/email-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 60, requiredPermission: 'View' }
            },
            {
                path: 'email-templates/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/email-templates/email-templates-description/email-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 60, requiredPermission: 'Edit' }
            },
            {
                path: 'email-templates/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/email-templates/email-templates-description/email-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 60, requiredPermission: 'Add' }
            },
            {
                path: 'campaigns/executions/logs/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/campaign-executions/campaign-logs/campaign-logs.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'View' }
            },
            {
                path: 'whatsapp-templates',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/whatsapp-templates/whatsapp-templates.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'View' }
            },
            {
                path: 'sms-templates',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/sms-templates/sms-templates.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'View' }
            },
            {
                path: 'notification-methods',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/notification-methods/notification-methods.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'View' }
            },
            {
                path: 'whatsapp-templates/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/whatsapp-templates/whatsapp-templates-description/whatsapp-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'View' }
            },
            {
                path: 'whatsapp-templates/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/whatsapp-templates/whatsapp-templates-description/whatsapp-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'Edit' }
            },
            {
                path: 'whatsapp-templates/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/whatsapp-templates/whatsapp-templates-description/whatsapp-templates-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'Add' }
            },
            {
                path: 'campaigns/executions',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/campaign-executions/campaign-executions.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'View' }
            },
            {
                path: 'campaigns/executions/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/campaign-executions/campaign-executions-description/campaign-executions-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'Add' }
            },
            {
                path: 'campaigns/executions/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/campaign-executions/campaign-executions-description/campaign-executions-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 66, requiredPermission: 'Edit' }
            },
            {
                path: 'module-group',
                loadChildren: () =>
                    import('app/modules/admin/pages/module-group/module-group.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 34, requiredPermission: 'View' }
            },
            {
                path: 'module-group/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/module-group/module-group-description/module-group-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 34, requiredPermission: 'View' }
            },
            {
                path: 'module-group/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/module-group/module-group-description/module-group-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 34, requiredPermission: 'Edit' }
            },
            {
                path: 'module-group/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/module-group/module-group-description/module-group-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 34, requiredPermission: 'Add' }
            },
            {
                path: 'modules',
                loadChildren: () =>
                    import('app/modules/admin/pages/module/module.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 35, requiredPermission: 'View' }
            },
            {
                path: 'modules/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/module/module-description/module-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 35, requiredPermission: 'View' }
            },
            {
                path: 'modules/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/module/module-description/module-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 35, requiredPermission: 'Edit' }
            },
            // Module Launches — dashboard "new module" cards. Shares the
            // Modules grant (a_modules 35); see module-launch.component.ts.
            {
                path: 'module-launches',
                loadChildren: () =>
                    import('app/modules/admin/pages/module-launch/module-launch.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 35, requiredPermission: 'View' }
            },
            // Admin permission catalog (a_modulegroups / a_modules) — the rows
            // that gate THIS panel's pages. Ids come from ADMIN_MODULE_IDS so
            // they stay aligned with the backend's reserved list and seed.
            {
                path: 'admin-module-group',
                loadChildren: () =>
                    import('app/modules/admin/pages/admin-module-group/admin-module-group.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULE_GROUPS, requiredPermission: 'View' }
            },
            {
                path: 'admin-module-group/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-module-group/admin-module-group-description/admin-module-group-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULE_GROUPS, requiredPermission: 'View' }
            },
            {
                path: 'admin-module-group/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-module-group/admin-module-group-description/admin-module-group-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULE_GROUPS, requiredPermission: 'Edit' }
            },
            {
                path: 'admin-module-group/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-module-group/admin-module-group-description/admin-module-group-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULE_GROUPS, requiredPermission: 'Add' }
            },
            {
                path: 'admin-modules',
                loadChildren: () =>
                    import('app/modules/admin/pages/admin-module/admin-module.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULES, requiredPermission: 'View' }
            },
            {
                path: 'admin-modules/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-module/admin-module-description/admin-module-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULES, requiredPermission: 'View' }
            },
            {
                path: 'admin-modules/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-module/admin-module-description/admin-module-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULES, requiredPermission: 'Edit' }
            },
            {
                path: 'admin-modules/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/admin-module/admin-module-description/admin-module-description.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: ADMIN_MODULE_IDS.ADMIN_MODULES, requiredPermission: 'Add' }
            },
            {
                path: 'features',
                loadChildren: () =>
                    import('app/modules/admin/pages/feature/feature.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 36, requiredPermission: 'View' }
            },
            {
                path: 'features/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-detail/feature-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 36, requiredPermission: 'View' }
            },
            {
                path: 'features/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-detail/feature-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 36, requiredPermission: 'Edit' }
            },
            {
                path: 'features/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-detail/feature-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 36, requiredPermission: 'Add' }
            },

            {
                path: 'feature-category',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-category/feature-category.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 37, requiredPermission: 'View' }
            },
            {
                path: 'feature-category/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-category-detail/feature-category-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 37, requiredPermission: 'View' }
            },
            {
                path: 'feature-category/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-category-detail/feature-category-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 37, requiredPermission: 'Edit' }
            },
            {
                path: 'feature-category/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feature-category-detail/feature-category-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 37, requiredPermission: 'Add' }
            },

            {
                path: 'blogtype',
                loadChildren: () =>
                    import('app/modules/admin/pages/blog-type/blogtype.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 13, requiredPermission: 'View' }
            },
            {
                path: 'FAQs',
                loadChildren: () =>
                    import(
                        'app/modules/admin/apps/help-center/faqs/faqs.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 26, requiredPermission: 'View' }
            },

            {
                path: 'partner',
                loadChildren: () =>
                    import('app/modules/admin/pages/partner/partner.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 4, requiredPermission: 'View' }
            },
            {
                path: 'sales-commission',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/sale-partner-flow/sale-partner-flow.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 71, requiredPermission: 'View' }
            },
            {
                path: 'partner-withdrawals',
                redirectTo: 'partner-payments',
                pathMatch: 'full',
            },
            {
                path: 'partner-payments',
                loadChildren: () => import('app/modules/admin/pages/partner/sale-partner-payouts/sale-partner-payouts.routes'),
                canActivate: [PermissionGuard],
                data: { anyModuleIds: [68, 70], requiredPermission: 'View' },
            },
            {
                // Partners past the minimum withdrawal amount who still
                // need documents or bank confirmed. Matters most when
                // automatic verification is OFF, where every row is a job
                // for a person and nothing else lists them.
                path: 'partner-verifications',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/sale-partner-verifications/sale-partner-verifications.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 69, requiredPermission: 'View' }
            },
            {
                path: 'partner-payouts',
                redirectTo: 'partner-payments',
                pathMatch: 'full',
            },
            {
                // Every partner's app-captured leads in one list. The same
                // rows appear per-partner on the partner detail Lead tab.
                path: 'partner-leads',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/partner-leads/partner-leads.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 4, requiredPermission: 'View' }
            },
            {
                path: 'partner-tds-config',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/sale-partner-tds-config/sale-partner-tds-config.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 72, requiredPermission: 'View' }
            },
            {
                path: 'partner-form16a',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/sale-partner-form16a/sale-partner-form16a.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 74, requiredPermission: 'View' }
            },
            {
                path: 'partner-tds-settlement',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/sale-partner-tds-settlement/sale-partner-tds-settlement.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 73, requiredPermission: 'View' }
            },
            {
                path: 'partner/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/partner-detail/partner-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 4, requiredPermission: 'View' }
            },
            {
                path: 'partner/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/partner-detail/partner-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 4, requiredPermission: 'Edit' }
            },
            {
                path: 'partner/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner/partner-detail/partner-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 4, requiredPermission: 'Add' }
            },
            {
                path: 'wallet',
                loadChildren: () =>
                    import('app/modules/admin/pages/wallet/wallet.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 11, requiredPermission: 'View' }
            },

            {
                path: 'leads',
                loadChildren: () =>
                    import('app/modules/admin/pages/leads/leads.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 5, requiredPermission: 'View' }
            },

            {
                path: 'leads/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/leads/lead-detail/lead-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 5, requiredPermission: 'View' }
            },

            {
                path: 'users',
                loadChildren: () =>
                    import('app/modules/admin/pages/users/users.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 21, requiredPermission: 'View' }
            },
            {
                path: 'users/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-detail/user-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 21, requiredPermission: 'View' }
            },
            {
                path: 'users/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-detail/user-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 21, requiredPermission: 'Edit' }
            },
            {
                path: 'users/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-detail/user-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 21, requiredPermission: 'Add' }
            },
            {
                path: 'user-roles',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-role/user-role.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 22, requiredPermission: 'View' }
            },

            {
                path: 'user-roles/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-role/user-role-detail/user-role-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 22, requiredPermission: 'View' }
            },
            {
                path: 'user-roles/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-role/user-role-detail/user-role-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 22, requiredPermission: 'Edit' }
            },
            {
                path: 'user-roles/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/user-role/user-role-detail/user-role-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 22, requiredPermission: 'Add' }
            },
            {
                path: 'business-roles',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-role/business-role.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 31, requiredPermission: 'View' }
            },
            {
                path: 'business-roles/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-role/business-role-detail/business-role-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 31, requiredPermission: 'View' }
            },
            {
                path: 'business-roles/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-role/business-role-detail/business-role-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 31, requiredPermission: 'Edit' }
            },
            {
                path: 'business-roles/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-role/business-role-detail/business-role-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 31, requiredPermission: 'Add' }
            },
            {
                path: 'tickets',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-and-support/ticket-and-support.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 14, requiredPermission: 'View' }
            },

            {
                path: 'tickets/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-and-support/ticket-and-support-detail/ticket-and-support-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 14, requiredPermission: 'View' }
            },
            {
                path: 'tickets/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-and-support/ticket-and-support-detail/ticket-and-support-details.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 14, requiredPermission: 'View' }
            },
            {
                path: 'tickets/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-and-support/ticket-and-support-crud/ticket-and-support-crud.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 14, requiredPermission: 'Edit' }
            },
            {
                path: 'tickets/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-and-support/ticket-and-support-crud/ticket-and-support-crud.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 14, requiredPermission: 'Add' }
            },

            {
                path: 'ticket-topics',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-topic/ticketTopic.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 29, requiredPermission: 'View' }
            },
            {
                path: 'plans',
                loadChildren: () =>
                    import('app/modules/admin/pages/pricing/plan.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'View' }
            },
            {
                // Import configuration — a feature's category, screen, counter
                // and every plan's tick/number/reset from one sheet, plus the
                // export that carries a tested dev configuration to production.
                //
                // MUST stay above 'plans/:mode' below: that route would
                // otherwise match this URL with mode = 'feature-import' and open
                // the plan editor instead. Routes are matched in order.
                path: 'plans/feature-import',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/pricing/feature-import/feature-import.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'View' }
            },
            {
                // Marketplace Catalog — per-plan commercial statuses + prices.
                path: 'marketplace-catalog',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/marketplace-catalog/marketplace-catalog.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'View' }
            },
            {
                // Business Add-ons — The Shop seller side (grant/cancel
                // marketplace add-ons per business). Same Plans-area guard.
                path: 'business-addons',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-addons/business-addons.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'View' }
            },
            {
                path: 'website-setting',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/website-setting/website-setting.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 25, requiredPermission: 'View' }
            },
            {
                path: 'plans/view/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/pricing/plan-detail/plan-detail.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'View' }
            },

            {
                path: 'plans/:mode/:id',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/pricing/plan-crud/plan-crud.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'Edit' }
            },

            {
                path: 'plans/:mode',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/pricing/plan-crud/plan-crud.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 7, requiredPermission: 'Add' }
            },

            {
                path: 'support-article-types',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/support-article-types/support-article-types.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 27, requiredPermission: 'View' }
            },
            {
                path: 'settings',
                loadChildren: () =>
                    import('app/modules/admin/pages/settings/settings.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 24, requiredPermission: 'View' }
            },
            {
                path: 'server-health',
                loadChildren: () =>
                    import('app/modules/admin/pages/server-health/server-health.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 67, requiredPermission: 'View' }
            },
            {
                // What Easy Khata pays outside vendors (Meta WhatsApp, live from
                // Meta). Rides the Settings grant (a_modules 24) — the page that
                // holds the WhatsApp credentials it reads with.
                path: 'third-party-services',
                loadChildren: () =>
                    import('app/modules/admin/pages/third-party-services/third-party-services.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 24, requiredPermission: 'View' }
            },
            {
                path: 'business-settings',
                loadChildren: () =>
                    import('app/modules/admin/pages/business-settings/business-settings.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 33, requiredPermission: 'View' }
            },
            {
                path: 'business-user-settings',
                loadChildren: () =>
                    import('app/modules/admin/pages/business-user-settings/business-user-settings.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 32, requiredPermission: 'View' }
            },
            {
                path: 'units',
                loadChildren: () =>
                    import('app/modules/admin/pages/units/units.routes'),
                canActivate: [PermissionGuard],
                data: {
                    moduleId: 38,
                    requiredPermission: 'View'
                }
            },
            {
                path: 'expense-categories',
                loadChildren: () =>
                    import('app/modules/admin/pages/expense-categories/expense-categories.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 43, requiredPermission: 'View' }
            },
            {
                path: 'gst-slabs',
                loadChildren: () =>
                    import('app/modules/admin/pages/gst-slabs/gst-slabs.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 44, requiredPermission: 'View' }
            },
            {
                path: 'hsn-codes',
                loadChildren: () =>
                    import('app/modules/admin/pages/hsn-codes/hsn-codes.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 45, requiredPermission: 'View' }
            },
            {
                path: 'business-category-settings',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-category-settings/business-category-settings.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 46, requiredPermission: 'View' } // Using module 46 (business categories) as base permission
            },
            {
                path: 'business-categories',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-categories/business-categories.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 46, requiredPermission: 'View' }
            },
            {
                // Which modules fit which business category (read-only; p91).
                path: 'category-module-map',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/category-module-map/category-module-map.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 76, requiredPermission: 'View' }
            },
            {
                path: 'business-types',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-types/business-types.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 47, requiredPermission: 'View' }
            },
            {
                path: 'business-questions',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-questions/business-questions.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 48, requiredPermission: 'View' }
            },
            {
                path: 'regions',
                loadChildren: () =>
                    import('app/modules/admin/pages/regions/regions.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 49, requiredPermission: 'View' }
            },
            {
                path: 'leave-types',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/leave-types/leave-types.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 50, requiredPermission: 'View' }
            },
            {
                path: 'currencies',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/currencies/currencies.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 51, requiredPermission: 'View' }
            },
            {
                path: 'campaign-channels',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/campaign-channels/campaign-channels.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 52, requiredPermission: 'View' }
            },
            {
                path: 'tax-types',
                loadChildren: () =>
                    import('app/modules/admin/pages/tax-types/tax-types.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 53, requiredPermission: 'View' }
            },
            {
                path: 'platforms',
                loadChildren: () =>
                    import('app/modules/admin/pages/platforms/platforms.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 54, requiredPermission: 'View' }
            },
            {
                path: 'auth-providers',
                loadChildren: () =>
                    import('app/modules/admin/pages/auth-providers/auth-providers.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 55, requiredPermission: 'View' }
            },
            {
                path: 'banks',
                loadChildren: () =>
                    import('app/modules/admin/pages/banks/banks.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 56, requiredPermission: 'View' }
            },
            {
                path: 'plan-users',
                loadChildren: () =>
                    import('app/modules/admin/pages/plan-users/plan-users.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 57, requiredPermission: 'View' }
            },
            {
                path: 'plan-companies',
                loadChildren: () =>
                    import('app/modules/admin/pages/plan-companies/plan-companies.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 58, requiredPermission: 'View' }
            },
            {
                path: 'stock-notes',
                loadChildren: () =>
                    import('app/modules/admin/pages/stock-notes/stock-notes.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 59, requiredPermission: 'View' }
            },
            {
                path: 'account-types',
                loadChildren: () =>
                    import('app/modules/admin/pages/account-type/account-type.routes'),
            },
            {
                path: 'languages',
                loadChildren: () =>
                    import('app/modules/admin/pages/languages/languages.routes'),
            },
            {
                path: 'scanned-documents',
                loadChildren: () =>
                    import('app/modules/admin/pages/scanned-documents/scanned-documents.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 62, requiredPermission: 'View' }
            },
            {
                path: 'scanned-documents/view/:id',
                loadChildren: () =>
                    import('app/modules/admin/pages/scanned-documents/scanned-documents-description/scanned-documents-description.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 62, requiredPermission: 'View' }
            },
            {
                path: 'scanned-documents/edit/:id',
                loadChildren: () =>
                    import('app/modules/admin/pages/scanned-documents/scanned-documents-description/scanned-documents-description.routes'),
                canActivate: [PermissionGuard],
                data: { moduleId: 62, requiredPermission: 'Edit' }
            },
            {
                path: 'all-reports',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/all-reports/all-reports.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-report/business-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                // Partner Reports → Payout Report. A report, so it lives with
                // the reports on moduleId 23 rather than with the partner
                // admin screens — reading a reconciliation should not require
                // the permission that edits payouts.
                path: 'partner-payout-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/partner-payout-report/partner-payout-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-paidunpaid-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-paidunpaid-report/business-paidunpaid-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-growth-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-growth-report/business-growth-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-revenue-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-revenue-report/business-revenue-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-user-engagement-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-user-engagement-report/business-user-engagement-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-active-inactive-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-active-inactive-report/business-active-inactive-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-activity-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-activity-report/business-activity-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-registered-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-registered-report/business-registered-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                // OTP Sends (Analytics & Logs): Firebase OTP requests per
                // customer, with a detail page per customer at /otp-sends/:key.
                // On the Reports grant (23) rather than a new a_modules row.
                path: 'otp-sends',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/otp-sends/otp-sends.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'business-new-vs-existing-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/business-new-vs-existing-report/business-new-vs-existing-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'opentickets-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/opentickets-report/opentickets-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'ticket-resolution-time-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-resolution-time-report/ticket-resolution-time-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'ticket-escalation-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-escalation-report/ticket-escalation-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'ticket-average-response-time-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/ticket-average-response-time-report/ticket-average-response-time-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'total-orders-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/total-orders-report/total-orders-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'pending-orders-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/pending-orders-report/pending-orders-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'order-conversion-rate-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/order-conversion-rate-report/order-conversion-rate-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'signup-to-sale-funnel-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/signup-to-sale-funnel-report/signup-to-sale-funnel-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'trial-conversion-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/trial-conversion-report/trial-conversion-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'sales-by-trial-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/sales-by-trial-report/sales-by-trial-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'cancelled-failed-order-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/cancelled-failed-order-report/cancelled-failed-order-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'sales-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/sales-report/sales-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-subscription-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-subscription-report/plan-subscription-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-renewal-expiration-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-renewal-expiration-report/plan-renewal-expiration-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-expiring-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-expiring-report/plan-expiring-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-expiring-summary-by-date-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-expiring-summary-by-date-report/plan-expiring-summary-by-date-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-expiring-revenue-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-expiring-revenue-report/plan-expiring-revenue-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-expired-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-expired-report/plan-expired-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-expired-summary-by-date-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-expired-summary-by-date-report/plan-expired-summary-by-date-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'plan-expired-revenue-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/plan-expired-revenue-report/plan-expired-revenue-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'inquiries-overview-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/inquiries-overview-report/inquiries-overview-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'inquiry-status-distribution-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/inquiry-status-distribution-report/inquiry-status-distribution-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'inquiry-conversion-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/inquiry-conversion-report/inquiry-conversion-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'feedback-status-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feedback-status-report/feedback-status-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'feedback-sentiment-analysis-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feedback-sentiment-analysis-report/feedback-sentiment-analysis-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'feedback-response-time-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feedback-response-time-report/feedback-response-time-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'feedback-resolution-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/feedback-resolution-report/feedback-resolution-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'total-merchant-referrals-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/total-merchant-referrals-report/total-merchant-referrals-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'merchant-referral-performance-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/merchant-referral-performance-report/merchant-referral-performance-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'sales-partner-customer-referrals-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/sales-partner-customer-referrals-report/sales-partner-customer-referrals-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },
            {
                path: 'sales-partner-performance-commission-report',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/sales-partner-performance-commission-report/sales-partner-performance-commission-report.routes'
                    ),
                canActivate: [PermissionGuard],
                data: { moduleId: 23, requiredPermission: 'View' }
            },

            {
                path: 'app-maintenance',
                loadChildren: () =>
                    import('app/modules/admin/pages/app-maintenance/app-maintenance.routes'),
                // Guarded like every other screen now that p86 gave it a module
                // row; before that the sidebar hid it but the URL was open to
                // any admin login.
                canActivate: [PermissionGuard],
                data: { moduleId: 64, requiredPermission: 'View' }
            },

            // 404 & Catch all
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-404/error-404.routes'
                    ),
            },
            { path: '**', redirectTo: 'dashboard' },
        ],
    },
];
